import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mantine/core';
import { PopupReferenceField } from './PopupReferenceField';
import { EntityBrowserDialog } from '../dialogs';
import { entityService } from '../../services/entityServiceSingleton';

export interface PersonReferenceSelection {
    id: string;
    displayName: string;
    /** Person subtype (e.g. employee, contractor) used for navigation and API fetch. */
    entityType: string;
}

export interface PersonReferenceFieldProps {
    label?: string;
    value: PersonReferenceSelection | null;
    onChange: (value: PersonReferenceSelection | null) => void;
    required?: boolean;
    disabled?: boolean;
    error?: boolean;
    description?: React.ReactNode;
    placeholder?: string;
}

/**
 * Single person/user reference: EntityBrowserDialog on Person with subtype filter (Employee, Student, …).
 */
export const PersonReferenceField: React.FC<PersonReferenceFieldProps> = ({
    label = 'User',
    value,
    onChange,
    required = false,
    disabled = false,
    error = false,
    description,
    placeholder = 'No user selected',
}) => {
    const [browserOpen, setBrowserOpen] = useState(false);
    const [resolvedDisplayName, setResolvedDisplayName] = useState('');
    const [fetchingSelected, setFetchingSelected] = useState(false);

    const failedFetchesRef = useRef<Set<string>>(new Set());
    const attemptedFetchesRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        const syncDisplayName = async () => {
            if (!value?.id) {
                setResolvedDisplayName('');
                failedFetchesRef.current.clear();
                attemptedFetchesRef.current.clear();
                return;
            }
            if (value.displayName?.trim()) {
                setResolvedDisplayName(value.displayName);
                return;
            }
            const fetchKey = `${value.entityType}:${value.id}`;
            if (attemptedFetchesRef.current.has(fetchKey)) {
                if (failedFetchesRef.current.has(fetchKey)) {
                    setResolvedDisplayName(value.id);
                    return;
                }
                if (!fetchingSelected) return;
            }
            if (fetchingSelected) return;
            attemptedFetchesRef.current.add(fetchKey);
            try {
                setFetchingSelected(true);
                const entityType = value.entityType?.trim() || 'Person';
                const full = await entityService.getEntityById(value.id, entityType, true, ['Id', 'DisplayName']);
                setResolvedDisplayName(full?.DisplayName || value.id);
                failedFetchesRef.current.delete(fetchKey);
            } catch (err: unknown) {
                const e = err as { response?: { status?: number }; status?: number; message?: string };
                setResolvedDisplayName(value.id);
                const statusCode = e?.response?.status ?? e?.status;
                const isNotFound = statusCode === 404 || e?.message?.toLowerCase().includes('not found');
                if (isNotFound) failedFetchesRef.current.add(fetchKey);
            } finally {
                setFetchingSelected(false);
            }
        };
        void syncDisplayName();
    }, [value, entityService, fetchingSelected]);

    const displayName = value ? (resolvedDisplayName || value.displayName || value.id) : '';

    const handleBrowserConfirm = (
        input: Array<{ Id: string; DisplayName?: string; EntityType?: string }> | string[]
    ) => {
        if (Array.isArray(input) && input.length > 0) {
            const first = input[0];
            if (typeof first === 'object' && first !== null && 'Id' in first) {
                const entity = first as { Id: string; DisplayName?: string; EntityType?: string };
                const entityType = (entity.EntityType ?? 'Person').trim();
                onChange({
                    id: entity.Id,
                    displayName: entity.DisplayName || entity.Id,
                    entityType,
                });
            }
        }
        setBrowserOpen(false);
    };

    return (
        <Box mb="md">
            <PopupReferenceField
                label={label}
                entityId={value?.id ?? null}
                displayName={displayName}
                viewEntityType={value?.entityType ?? 'Person'}
                viewEntityLabel={value?.entityType ?? 'user'}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                error={error}
                description={description}
                onSelect={() => setBrowserOpen(true)}
                onClear={() => onChange(null)}
                idEntry={{
                    entityTypes: ['Person'],
                    entityId: value?.id ?? null,
                    displayName,
                    onCommit: (entity) =>
                        onChange({
                            id: entity.Id,
                            displayName: entity.DisplayName,
                            entityType: entity.EntityType,
                        }),
                    onClear: () => onChange(null),
                }}
            />
            <EntityBrowserDialog
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onConfirm={handleBrowserConfirm}
                entityType="Person"
                title="Select User"
                callerName="PersonReferenceField"
                singleSelection={true}
            />
        </Box>
    );
};
